import {
  BidStatusTransitionTypeDto,
  type BuyerBidDetailDto,
  type BidStatusTransitionDto,
} from "../../../autogen/bff-api";

export type BidActivityType = "submitted" | "opened" | "rejected" | "rejectionRevoked" | "awarded";

export interface BidActivityEvent {
  type: BidActivityType;
  at: string;
  by?: string | null;
  reason?: string | null;
}

const typeOrder: Record<BidActivityType, number> = {
  submitted: 0,
  opened: 1,
  rejected: 2,
  rejectionRevoked: 3,
  awarded: 4,
};

const compareInstants = (a: string, b: string): number => Date.parse(a) - Date.parse(b);

export const latestTransition = (
  transitions: BidStatusTransitionDto[] | undefined,
  type: BidStatusTransitionTypeDto,
): BidStatusTransitionDto | undefined =>
  [...(transitions ?? [])]
    .filter((transition) => transition.type === type)
    .sort((a, b) => compareInstants(b.at, a.at))[0];

export const getBidActivityEvents = (detail: BuyerBidDetailDto): BidActivityEvent[] => {
  const transitions = detail.bid.statusHistory ?? [];
  const submittedAt =
    latestTransition(transitions, BidStatusTransitionTypeDto.Submitted)?.at ?? detail.bid.submittedAt;
  const awardedAt = latestTransition(transitions, BidStatusTransitionTypeDto.Awarded)?.at;
  const rejections: BidActivityEvent[] = transitions
    .filter((transition) => transition.type === BidStatusTransitionTypeDto.Rejected)
    .map((transition) => ({ type: "rejected", at: transition.at }));
  const revocations: BidActivityEvent[] = (detail.bid.rejectionRevocations ?? []).map((revocation) => ({
    type: "rejectionRevoked",
    at: revocation.at,
    by: revocation.by,
    reason: revocation.reason ?? null,
  }));

  const candidates: (BidActivityEvent | null)[] = [
    submittedAt ? { type: "submitted", at: submittedAt } : null,
    detail.bidOpenedAt ? { type: "opened", at: detail.bidOpenedAt } : null,
    ...rejections,
    ...revocations,
    awardedAt ? { type: "awarded", at: awardedAt } : null,
  ];
  const events = candidates.filter((event): event is BidActivityEvent => event != null);

  return events.sort((a, b) => {
    const byTimestamp = compareInstants(a.at, b.at);
    return byTimestamp !== 0 ? byTimestamp : typeOrder[a.type] - typeOrder[b.type];
  });
};
